import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PblCaseList from '@/components/Pbl/CaseList';
import {
  addPblDashboardFilterOption,
  getPblDashboardCaseFilter,
  getPblDashboardCaseStatuses,
  isPblDashboardCaseFilter,
  PBL_DASHBOARD_FILTER_PARAM,
} from '@/components/Pbl/CaseList/dashboardFilter';
import type { PblCaseListFilter } from '@/components/Pbl/CaseList/types';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import PATH from '@/router/path';
import useTableRequestState from '@/hooks/useTableRequestState';
import { isPblLawyer } from '@/utils/pblRole';
import { translatePblDashboardLabel } from '@/utils/pblDisplayTranslation';
import { errorHandler } from '@/utils/toastHandler';

import { mapPblCaseToOcManagedCase } from '../OcManageCases/caseReviewState';
import type { PblOcCaseFilter, PblOcManagedCase, PblOcManagedCaseStatus } from '../OcManageCases/types';
import { isLawyerCurrentCase, isPendingLawyerConfirmationCase } from './caseVisibility';
import { loadAllLawyerCases } from './loadAllLawyerCases';
import { useTranslation } from 'react-i18next';
import type { PblCase } from '@/types/pbl';
import { usePblClock } from '@/hooks/usePblClock';
import { isPblCaseClosureDue } from '../CaseProgressDetail/closureDue';

const buildCaseDetailPath = (caseId: string | number) => PATH.pbl.caseDetail.replace(':caseId', String(caseId));

const isPendingLawyerTimeslotCase = (item: PblOcManagedCase) => item.backendStatus === 'pending_lawyer_timeslot';

const PblLawyerManageCases = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardFilter = getPblDashboardCaseFilter(searchParams);
  const auth = useAppSelector(getAuthSelector);
  const isLawyer = isPblLawyer(auth.userData);
  const [caseRecords, setCaseRecords] = useState<PblCase[]>([]);
  const now = usePblClock();
  const rows = useMemo(() => caseRecords.map((record) => ({
    ...mapPblCaseToOcManagedCase(record),
    attentionLabel: isPblCaseClosureDue(record, auth.userData, now) ? t('pbl.backlogContinuation.closureDue') : undefined,
  })), [caseRecords, auth.userData, now, t]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const { requestState, beginRequest, completeRequest, failRequest, retryKey, retry } = useTableRequestState(
    rows.length > 0
  );
  const statusLabels = useMemo<Record<PblOcManagedCaseStatus, string>>(
    () => ({
      in_progress: t('pbl.pagesHardcoded.lawyerManageCases.inProgress'),
      to_be_rated: t('pbl.pagesHardcoded.lawyerManageCases.toBeRated'),
      case_closed: t('pbl.pagesHardcoded.lawyerManageCases.caseClosed'),
      case_cancelled: t('pbl.dashboardCalendarNotifications.dashboard.caseTable.statuses.caseClosed'),
      to_be_confirmed: t('pbl.dashboardCalendarNotifications.dashboard.caseTable.statuses.toBeConfirmed'),
    }),
    [t]
  );
  const filters = useMemo<Array<{ value: PblOcCaseFilter; label: string }>>(
    () => [
      { value: 'all', label: t('pbl.pagesHardcoded.lawyerManageCases.all') },
      { value: 'in_progress', label: t('pbl.pagesHardcoded.lawyerManageCases.inProgress') },
      { value: 'to_be_rated', label: t('pbl.pagesHardcoded.lawyerManageCases.toBeRated') },
      { value: 'case_closed', label: t('pbl.pagesHardcoded.lawyerManageCases.caseClosed') },
    ],
    [t]
  );
  const activeFilters = useMemo(
    () =>
      addPblDashboardFilterOption(
        filters,
        dashboardFilter,
        dashboardFilter ? translatePblDashboardLabel(t, { key: dashboardFilter, label: dashboardFilter }) : ''
      ),
    [dashboardFilter, filters, t]
  );

  useEffect(() => {
    const loadCases = async () => {
      const requestId = beginRequest();
      try {
        const dashboardStatuses = getPblDashboardCaseStatuses(dashboardFilter);
        const cases = await loadAllLawyerCases(dashboardStatuses?.length === 1 ? dashboardStatuses[0] : undefined);

        if (!completeRequest(requestId)) return;

        const tableCases = cases.filter(
          (item) => isLawyerCurrentCase(item) || isPendingLawyerConfirmationCase(item, auth.userData)
        );

        setCaseRecords(tableCases);
        setTotalEntries(tableCases.length);
        setPendingReviewCount(cases.filter((item) => isPendingLawyerConfirmationCase(item, auth.userData)).length);
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.data ||
          t('pbl.pagesHardcoded.lawyerManageCases.failedToLoadCases');
        if (failRequest(requestId, message)) errorHandler(message);
      }
    };

    loadCases();
  }, [auth.userData, beginRequest, completeRequest, dashboardFilter, failRequest, retryKey, t]);

  const updateFilter = (filter: PblCaseListFilter) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (isPblDashboardCaseFilter(filter)) {
      nextSearchParams.set(PBL_DASHBOARD_FILTER_PARAM, filter);
    } else {
      nextSearchParams.delete(PBL_DASHBOARD_FILTER_PARAM);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const goToDetail = (item: PblOcManagedCase) => {
    if (item.backendStatus === 'lawyer_invited' || item.backendStatus === 'pending_lawyer_response') {
      navigate(PATH.pbl.lawyerPendingReviewDetail.replace(':caseId', String(item.id)));
      return;
    }
    if (isPendingLawyerTimeslotCase(item)) {
      if (isLawyer) {
        navigate(buildCaseDetailPath(item.id));
      }
      return;
    }

    navigate(buildCaseDetailPath(item.id));
  };

  const isRowClickable = (item: PblOcManagedCase) => !isPendingLawyerTimeslotCase(item) || isLawyer;

  return (
    <PblCaseList
      title={t('pbl.pagesHardcoded.lawyerManageCases.pendingReviewList.manageCase')}
      rows={rows}
      filters={activeFilters}
      initialFilter={dashboardFilter || 'all'}
      onFilterChange={updateFilter}
      primaryAction={{
        label: t('pbl.pagesHardcoded.lawyerManageCases.toBeConfirmedValue', {
          value1:
            requestState.status === 'loading' || (requestState.status === 'error' && !requestState.hasStaleData)
              ? '—'
              : pendingReviewCount,
        }),
        onClick: () => navigate(PATH.pbl.lawyerPendingReviewCases),
      }}
      totalEntries={totalEntries}
      requestState={requestState}
      onRetry={retry}
      searchAriaLabel="Search cases"
      statusLabels={statusLabels}
      isRowClickable={isRowClickable}
      onRowClick={goToDetail}
    />
  );
};

export default PblLawyerManageCases;
